/**
 * BootcampAdminDashboard — facilitator review queue.
 *
 * Consumes ``/education/bootcamp/admin/review_queue/`` and
 * ``/education/bootcamp/admin/reviews/<enrollment_id>/<week_number>/``.
 *
 * Shows every week-progress row in the ``submitted`` state with the
 * student's name, week, and submission time. Opens a compact review
 * drawer with a notes textarea and Pass / Retry buttons.
 *
 * Only visible to admins — the parent (AdminMentorshipView or wherever
 * this ends up mounting) is responsible for the role gate. If a
 * non-admin loads the component directly, the backend's admin check
 * returns 403 and we surface it as an error.
 */

import { useCallback, useEffect, useState } from 'react';
import {
    CheckCircle2,
    ClipboardList,
    Clock,
    RotateCcw,
    UserCheck,
    X as XIcon,
} from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import EmptyState from '../ui/EmptyState';
import { Skeleton } from '../Skeleton';
import { useToastContext } from '../ToastProvider';
import {
    getReviewQueue,
    reviewDeliverables,
    type BootcampReviewQueueItem,
} from '../../bootcampService';
import './BootcampAdminDashboard.css';

function BootcampAdminDashboard() {
    const [items, setItems] = useState<BootcampReviewQueueItem[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [active, setActive] = useState<BootcampReviewQueueItem | null>(null);
    const { success, error: toastError } = useToastContext();

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const rows = await getReviewQueue();
            setItems(rows);
        } catch (err) {
            console.error('[BootcampAdminDashboard] load failed', err);
            setError("Couldn't load the review queue. Try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    const handleReviewed = useCallback(async (
        item: BootcampReviewQueueItem,
        decision: 'pass' | 'retry',
        notes: string,
    ) => {
        try {
            await reviewDeliverables(item.enrollment_id, item.week_number, decision, notes);
            success(
                decision === 'pass'
                    ? `Passed ${item.user_display} — Week ${item.week_number}`
                    : `Retry requested — Week ${item.week_number}`,
            );
            setActive(null);
            await load();
        } catch (err) {
            console.error('[BootcampAdminDashboard] review failed', err);
            toastError("Couldn't submit the review decision. Please try again.");
        }
    }, [load, success, toastError]);

    return (
        <div className="bcmp-admin">
            <header className="bcmp-admin__header">
                <h2 className="bcmp-admin__title">
                    <UserCheck size={22} aria-hidden="true" /> Bootcamp review queue
                </h2>
                <p className="bcmp-admin__subtitle">
                    Every week-deliverable submission awaiting facilitator review.
                    Pass to unlock the next week; retry to request changes with notes.
                </p>
            </header>

            {loading ? (
                <div className="bcmp-admin__skeletons">
                    <Skeleton width="100%" height="72px" borderRadius="12px" />
                    <Skeleton width="100%" height="72px" borderRadius="12px" />
                    <Skeleton width="100%" height="72px" borderRadius="12px" />
                </div>
            ) : error ? (
                <EmptyState
                    icon={<XIcon size={26} />}
                    title="Couldn't load queue"
                    description={error}
                    action={
                        <Button variant="secondary" size="md" onClick={() => void load()}>
                            Try again
                        </Button>
                    }
                />
            ) : items && items.length === 0 ? (
                <EmptyState
                    icon={<ClipboardList size={26} />}
                    title="Queue is empty"
                    description="No pending reviews. Come back when a student submits their next week."
                />
            ) : (
                <ul className="bcmp-admin__list">
                    {items?.map((item) => (
                        <li key={item.progress_id}>
                            <Card
                                variant="row"
                                interactive
                                onClick={() => setActive(item)}
                            >
                                <Card.Header>
                                    <div className="bcmp-admin__item-week" aria-hidden="true">
                                        Week {item.week_number}
                                    </div>
                                    <div className="bcmp-admin__item-body">
                                        <p className="bcmp-admin__item-user">{item.user_display}</p>
                                        <p className="bcmp-admin__item-course">{item.course_title}</p>
                                        <p className="bcmp-admin__item-time">
                                            <Clock size={12} aria-hidden="true" />
                                            {formatTime(item.deliverables_submitted_at)}
                                        </p>
                                    </div>
                                    <Button variant="primary" size="sm">
                                        Review
                                    </Button>
                                </Card.Header>
                            </Card>
                        </li>
                    ))}
                </ul>
            )}

            {active && (
                <ReviewDrawer
                    item={active}
                    onClose={() => setActive(null)}
                    onDecision={handleReviewed}
                />
            )}
        </div>
    );
}

// ── Review drawer ────────────────────────────────────────────────────────

type DrawerProps = {
    item: BootcampReviewQueueItem;
    onClose: () => void;
    onDecision: (
        item: BootcampReviewQueueItem,
        decision: 'pass' | 'retry',
        notes: string,
    ) => Promise<void>;
};

function ReviewDrawer({ item, onClose, onDecision }: DrawerProps) {
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState<'pass' | 'retry' | null>(null);

    const handleClick = useCallback(async (decision: 'pass' | 'retry') => {
        if (decision === 'retry' && notes.trim().length < 10) {
            // The student needs something actionable — don't allow a
            // silent retry.
            return;
        }
        setBusy(decision);
        try {
            await onDecision(item, decision, notes.trim());
        } finally {
            setBusy(null);
        }
    }, [item, notes, onDecision]);

    return (
        <Modal
            open
            onClose={onClose}
            size="md"
            placement="right"
            ariaLabel={`Review ${item.user_display} — Week ${item.week_number}`}
        >
            <Modal.Header onClose={onClose}>
                Review · {item.user_display} · Week {item.week_number}
            </Modal.Header>
            <Modal.Body>
                <p className="bcmp-admin__drawer-course">{item.course_title}</p>
                <p className="bcmp-admin__drawer-meta">
                    Submitted {formatTime(item.deliverables_submitted_at)}
                </p>

                <label className="bcmp-admin__drawer-field">
                    <span>Reviewer notes</span>
                    <span className="bcmp-admin__drawer-hint">
                        Required for retry. Optional for pass — but praise lands too.
                    </span>
                    <textarea
                        rows={6}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="What stood out? If requesting retry, be specific about what to change."
                        disabled={busy !== null}
                    />
                </label>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    size="md"
                    icon={<RotateCcw size={16} />}
                    onClick={() => void handleClick('retry')}
                    disabled={busy !== null || notes.trim().length < 10}
                >
                    {busy === 'retry' ? 'Sending…' : 'Request retry'}
                </Button>
                <Button
                    variant="primary"
                    size="md"
                    icon={<CheckCircle2 size={16} />}
                    onClick={() => void handleClick('pass')}
                    disabled={busy !== null}
                >
                    {busy === 'pass' ? 'Passing…' : 'Pass'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function formatTime(iso: string | null): string {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    } catch {
        return '—';
    }
}

export default BootcampAdminDashboard;
